import React from 'react';
import { Box, Typography, Paper, CardContent, CardActionArea, Stack, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import {
    People as PeopleIcon,
    EventAvailable as AttendanceIcon,
    EventNote as LeaveIcon,
    AccountBalance as PayrollIcon,
    ArrowForwardIos as ArrowIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const API_URL = process.env.REACT_APP_API_URL1;

const toFilenamePart = (value) => String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_');

const modules = [
    {
        title: 'Personnel & Organization',
        icon: <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
        color: 'primary.main',
        reports: [
            { name: 'Custom Staff Report', path: '/reports/custom-personnel', description: 'Build customized directory exports by selecting specific fields, departments, or branches.', status: 'ready' },
            { name: 'Staff Demographic Analytics', path: '/reports/demographics', description: 'Visual breakdown of gender, race, religion, age, and service years.', status: 'ready' },
            { name: 'Document Expiry Report', path: '/reports/document-expiry', description: 'Monitor NRC, Passport, and other document validity.', status: 'ready' },
            { name: 'Staff Movement & Events', path: '/reports/staff-movement', description: 'Track employee entries, exits, department transfers, and promotions within a date range.', status: 'ready' },
            { name: 'Cessation & Turnover', path: '/reports/turnover', description: 'Track staff exits and attrition rates.', status: 'ready' },
        ]
    },
    {
        title: 'Attendance & Time',
        icon: <AttendanceIcon sx={{ fontSize: 32, color: '#10b981' }} />,
        color: '#10b981',
        reports: [
            { name: 'Overtime Submission & Source Audit', path: '/reports/ot-submission-audit', description: 'Comprehensive audit trail of OT submissions across Mobile Claims, HR Direct Entries, and Auto-Capture.', status: 'ready' },
            { name: 'Manual Attendance Requests', path: '/reports/manual-requests', description: 'Audit trail of all manual clock-in/out corrections.', status: 'ready' },
            { name: 'Daily Attendance Report', path: 'hst-modal', description: 'Download daily attendance format.', status: 'ready' },
            { name: 'Daily Attendance Matrix', path: '/reports/attendance-matrix', description: 'Grid matrix displaying daily presence, clock-in times, holidays, rest days, and leaves.', status: 'ready' },
            { name: 'Daily Attendance Summary', path: '/reports/daily-attendance', description: 'Real-time snapshot of today\'s presence and lateness.', status: 'ready' },
        ]
    },
    {
        title: 'Leave Management',
        icon: <LeaveIcon sx={{ fontSize: 32, color: '#f59e0b' }} />,
        color: '#f59e0b',
        reports: [
            { name: 'Leave Balance Summary', path: '/leave/balances', description: 'Current entitlements and remaining balances.', status: 'ready' },
            { name: 'Leave Summary Report', path: '/reports/leave-summary', description: 'Aggregated leave days taken by staff per leave type with min-days filters.', status: 'ready' },
            { name: 'Leave Calendar', path: '/reports/leave-calendar', description: 'Interactive visual monthly calendar showing counts and detailed list of staff on approved leaves.', status: 'ready' },
            { name: 'Leave Analytics Dashboard', path: '/reports/leave-analytics', description: 'Visual breakdown of leave days taken by type, department, and status in pie and bar charts.', status: 'ready' },
            { name: 'Leave History Audit', path: '/reports/leave-history', description: 'Audit leave combinations, adjacent rest days, sandwich sequences, dates, and approvals.', status: 'ready' },
        ]
    },
    {
        title: 'Payroll & Costs',
        icon: <PayrollIcon sx={{ fontSize: 32, color: '#ef4444' }} />,
        color: '#ef4444',
        reports: [
            { name: 'Payroll Register', path: 'payroll-register-modal', description: 'Employee-level payroll register with earnings, deductions, gross pay, net pay, and dynamic pay elements.', status: 'ready' },
            { name: 'Payroll Summary by Department / Branch / Cost Centre', path: 'payroll-summary-modal', description: 'Aggregated payroll totals grouped by department, branch, and cost centre in one workbook.', status: 'ready' },
            { name: 'CTC Report', path: 'payroll-ctc-modal', description: 'Company cost view with employee context, dynamic earning elements, employer contributions, CTC total, and annualized CTC.', status: 'ready' },
            { name: 'Payroll Reconciliation Report', path: 'payroll-reconciliation-modal', description: 'Compare two finalized payroll runs by pay element with variance amount, variance percent, and automatic remarks.', status: 'ready' },
            { name: 'IRD PAYE Monthly Tax Report', path: 'payroll-ird-paye-modal', description: 'Monthly PAYE withholding report in the official IRD Excel template format.', status: 'ready' },
            { name: 'Bank Payroll Export', path: 'bank-modal', description: 'Export payroll data in specific bank formats (e.g., UAB Bank).', status: 'ready' },
            { name: 'SSB Monthly Report (Form 13)', path: 'payroll-ssb-modal', description: 'Monthly SSB contribution report in Myanmar language template.', status: 'ready' },
            { name: 'Monthly Salary Preview', path: '/reports/payroll-preview', description: 'Verification report before finalizing payroll.', status: 'planned' },
            { name: 'Pay Element Analysis', path: '/reports/payroll-elements', description: 'Breakdown of earnings and deductions.', status: 'planned' },
        ]
    }
];

const ReportsDashboard = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [hstDialogOpen, setHstDialogOpen] = React.useState(false);
    const [hstDateFrom, setHstDateFrom] = React.useState(new Date().toISOString().slice(0, 10));
    const [hstDateTo, setHstDateTo] = React.useState(new Date().toISOString().slice(0, 10));
    const [hstBranchId, setHstBranchId] = React.useState('');
    const [hstOrgNodeId, setHstOrgNodeId] = React.useState('');
    const [hstBranches, setHstBranches] = React.useState([]);
    const [hstDepartments, setHstDepartments] = React.useState([]);
    const [isDownloading, setIsDownloading] = React.useState(false);

    // Bank Export States
    const [bankDialogOpen, setBankDialogOpen] = React.useState(false);
    const [bankRuns, setBankRuns] = React.useState([]);
    const [bankAccounts, setBankAccounts] = React.useState([]);
    const [selectedRun, setSelectedRun] = React.useState('');
    const [selectedPreviousRun, setSelectedPreviousRun] = React.useState('');
    const [selectedBankFormat, setSelectedBankFormat] = React.useState('UAB');
    const [selectedAccount, setSelectedAccount] = React.useState('');
    const [payrollReportDialogOpen, setPayrollReportDialogOpen] = React.useState(false);
    const [payrollReportType, setPayrollReportType] = React.useState('register');

    const fetchBankData = async () => {
        try {
            const tenant = localStorage.getItem("tenant") || "";
            const headers = { "X-Tenant": tenant };
            
            const runsRes = await fetch(`${API_URL}/web-reports/payroll/finalized-runs`, { credentials: "include", headers });
            if (runsRes.ok) setBankRuns(await runsRes.json());
            
            const accsRes = await fetch(`${API_URL}/web-reports/payroll/bank-accounts`, { credentials: "include", headers });
            if (accsRes.ok) setBankAccounts(await accsRes.json());
        } catch (e) {
            console.error("Failed to fetch bank export data", e);
        }
    };

    const openPayrollReportDialog = async (type) => {
        setPayrollReportType(type);
        await fetchBankData();
        setPayrollReportDialogOpen(true);
    };

    const fetchHstFilters = async () => {
        try {
            const tenant = localStorage.getItem("tenant") || "";
            const headers = { "X-Tenant": tenant };
            const [branchesRes, nodesRes] = await Promise.all([
                fetch(`${API_URL}/som/branches`, { credentials: "include", headers }),
                fetch(`${API_URL}/som/nodes`, { credentials: "include", headers })
            ]);

            if (branchesRes.ok) {
                const data = await branchesRes.json();
                setHstBranches(Array.isArray(data?.data) ? data.data : []);
            }
            if (nodesRes.ok) {
                const data = await nodesRes.json();
                setHstDepartments(Array.isArray(data?.data) ? buildHierarchicalList(data.data) : []);
            }
        } catch (e) {
            console.error("Failed to fetch Daily Attendance filters", e);
        }
    };

    const handlePayrollReportDownload = async () => {
        if (!selectedRun || !payrollReportType) return;
        if (payrollReportType === 'reconciliation' && !selectedPreviousRun) return;
        setIsDownloading(true);
        try {
            const tenant = localStorage.getItem("tenant") || "";
            const reportEndpoints = {
                register: 'export-register',
                summary: 'export-summary',
                ctc: 'export-ctc',
                reconciliation: 'export-reconciliation',
                irdPaye: 'export-ird-paye',
                ssb: 'export-ssb',
            };
            const endpoint = reportEndpoints[payrollReportType] || 'export-register';
            const query = payrollReportType === 'reconciliation'
                ? `previousRunId=${selectedPreviousRun}&currentRunId=${selectedRun}`
                : `runId=${selectedRun}`;
            const res = await fetch(`${API_URL}/web-reports/payroll/${endpoint}?${query}`, {
                credentials: "include",
                headers: { "X-Tenant": tenant },
            });

            if (!res.ok) throw new Error(`Failed to export payroll ${payrollReportType}`);

            const blob = await res.blob();
            const a = document.createElement("a");
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            const run = bankRuns.find(r => String(r.id) === String(selectedRun));
            const periodNameFormatted = run ? `${run.period_name.replace(' ', '_')}_${run.run_type}` : selectedRun;
            const reportFilenames = {
                register: `Payroll_Register_${periodNameFormatted}.xlsx`,
                summary: `Payroll_Summary_${periodNameFormatted}.xlsx`,
                ctc: `CTC_Report_${periodNameFormatted}.xlsx`,
                reconciliation: `Payroll_Reconciliation_${periodNameFormatted}.xlsx`,
                irdPaye: `IRD_PAYE_${periodNameFormatted}.xlsx`,
                ssb: `SSB_Monthly_Report_${periodNameFormatted}.xlsx`,
            };
            a.download = reportFilenames[payrollReportType] || `Payroll_Report_${periodNameFormatted}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
            setPayrollReportDialogOpen(false);
        } catch (e) {
            alert(e.message || "Export failed");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleBankExport = async () => {
        if (!selectedRun || !selectedBankFormat) return;
        setIsDownloading(true);
        try {
            const tenant = localStorage.getItem("tenant") || "";
            const res = await fetch(`${API_URL}/web-reports/payroll/export-bank?runId=${selectedRun}&bankFormat=${selectedBankFormat}&accountId=${selectedAccount}`, {
                credentials: "include",
                headers: { "X-Tenant": tenant },
            });

            if (!res.ok) throw new Error("Failed to export bank payroll format");

            const blob = await res.blob();
            const a = document.createElement("a");
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            const run = bankRuns.find(r => String(r.id) === String(selectedRun));
            const periodNameFormatted = run ? run.period_name.replace(' ', '_') : selectedRun;
            a.download = `BankExport_${selectedBankFormat}_Run_${periodNameFormatted}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
            setBankDialogOpen(false);
        } catch (e) {
            alert(e.message || "Export failed");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleHstDownload = async () => {
        if (!hstDateFrom || !hstDateTo) return;
        setIsDownloading(true);
        try {
            const tenant = localStorage.getItem("tenant") || "";
            const params = new URLSearchParams({
                start_date: hstDateFrom,
                end_date: hstDateTo
            });
            if (hstBranchId) params.set("branch_id", hstBranchId);
            if (hstOrgNodeId) params.set("org_node_id", hstOrgNodeId);

            const res = await fetch(`${API_URL}/web-reports/hst-daily-attendance/excel?${params.toString()}`, {
                credentials: "include",
                headers: { "X-Tenant": tenant },
            });

            if (!res.ok) throw new Error("Failed to export");

            const blob = await res.blob();
            const a = document.createElement("a");
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            const selectedBranch = hstBranches.find(branch => String(branch.id) === String(hstBranchId));
            const tenantCode = toFilenamePart(tenant || "TENANT");
            const branchName = selectedBranch ? toFilenamePart(selectedBranch.branch_name) : '';
            const branchPart = branchName ? `_${branchName}` : '';
            a.download = `${tenantCode}_Daily_Attendance${branchPart}_${hstDateFrom}_to_${hstDateTo}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
            setHstDialogOpen(false);
        } catch (e) {
            alert(e.message || "Export failed");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleReportClick = (report) => {
        if (report.status !== 'ready') return;
        if (report.path === 'hst-modal') {
            fetchHstFilters();
            setHstDialogOpen(true);
        } else if (report.path === 'bank-modal') {
            fetchBankData();
            setBankDialogOpen(true);
        } else if (report.path === 'payroll-register-modal') {
            openPayrollReportDialog('register');
        } else if (report.path === 'payroll-summary-modal') {
            openPayrollReportDialog('summary');
        } else if (report.path === 'payroll-ctc-modal') {
            openPayrollReportDialog('ctc');
        } else if (report.path === 'payroll-reconciliation-modal') {
            openPayrollReportDialog('reconciliation');
        } else if (report.path === 'payroll-ird-paye-modal') {
            openPayrollReportDialog('irdPaye');
        } else if (report.path === 'payroll-ssb-modal') {
            openPayrollReportDialog('ssb');
        } else {
            navigate(report.path);
        }
    };

    return (
        <Box sx={{ height: { md: '100vh' }, minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: { md: 'hidden' } }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', maxWidth: 1600, mx: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: { xs: 2, md: 2.5 }, flexShrink: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', mb: 0.5 }}>Reporting Hub</Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>Access all modular insights and data exports across the PeopleHub ecosystem.</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gridTemplateRows: { md: 'repeat(2, minmax(0, 1fr))' },
                        gap: { xs: 2, md: 2.5 },
                        flex: 1,
                        minHeight: 0,
                        overflow: { xs: 'visible', md: 'hidden' },
                    }}
                >
                    {modules.map((mod, idx) => (
                        <Paper
                            key={idx}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                minHeight: { xs: 280, md: 0 },
                                height: '100%',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                                <Box sx={{ px: 2.25, py: 1.75, bgcolor: '#fff', borderBottom: `4px solid ${mod.color}`, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                                    {mod.icon}
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>{mod.title}</Typography>
                                </Box>
                                <CardContent sx={{ p: 0, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                    <Stack divider={<Divider />}>
                                        {mod.reports.map((report, rIdx) => (
                                            <CardActionArea
                                                key={rIdx}
                                                onClick={() => handleReportClick(report)}
                                                sx={{
                                                    px: 2,
                                                    py: 1.35,
                                                    opacity: report.status === 'ready' ? 1 : 0.6,
                                                    cursor: report.status === 'ready' ? 'pointer' : 'default'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', mb: 0.35, lineHeight: 1.25 }}>
                                                            {report.name}
                                                            {report.status === 'planned' && (
                                                                <Typography component="span" variant="caption" sx={{ ml: 1, bgcolor: '#f1f5f9', color: '#94a3b8', px: 1, py: 0.2, borderRadius: 1, fontWeight: 800 }}>PLANNED</Typography>
                                                            )}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.35 }}>{report.description}</Typography>
                                                    </Box>
                                                    {report.status === 'ready' && <ArrowIcon sx={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }} />}
                                                </Box>
                                            </CardActionArea>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Paper>
                    ))}
                </Box>
            </Box>

            <Dialog open={hstDialogOpen} onClose={() => setHstDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Download Daily Attendance</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Select a date range to export the daily attendance report in HST format.
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            type="date"
                            fullWidth
                            value={hstDateFrom}
                            onChange={(e) => setHstDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            label="Start Date"
                        />
                        <TextField
                            type="date"
                            fullWidth
                            value={hstDateTo}
                            onChange={(e) => setHstDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            label="End Date"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Branch</InputLabel>
                            <Select
                                value={hstBranchId}
                                label="Branch"
                                onChange={(e) => setHstBranchId(e.target.value)}
                            >
                                <MenuItem value="">All Branches</MenuItem>
                                {hstBranches.map(branch => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.branch_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Department / SOM</InputLabel>
                            <Select
                                value={hstOrgNodeId}
                                label="Department / SOM"
                                onChange={(e) => setHstOrgNodeId(e.target.value)}
                            >
                                <MenuItem value="">All Departments</MenuItem>
                                {hstDepartments.map(node => (
                                    <MenuItem key={node.id} value={node.id}>
                                        {'\u00A0'.repeat((node._depth || 0) * 4)}{node.node_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setHstDialogOpen(false)} color="inherit" disabled={isDownloading}>Cancel</Button>
                    <Button onClick={handleHstDownload} variant="contained" disabled={isDownloading || !hstDateFrom || !hstDateTo}>
                        {isDownloading ? 'Exporting...' : 'Export HST'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payroll Standard Reports Dialog */}
            <Dialog open={payrollReportDialogOpen} onClose={() => setPayrollReportDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {payrollReportType === 'summary'
                        ? 'Payroll Summary'
                        : payrollReportType === 'ctc'
                            ? 'CTC Report'
                            : payrollReportType === 'reconciliation'
                                ? 'Payroll Reconciliation'
                                : payrollReportType === 'irdPaye'
                                    ? 'IRD PAYE Monthly Tax Report'
                                : 'Payroll Register'}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        {payrollReportType === 'reconciliation'
                            ? 'Select previous and current finalized payroll runs to download the variance report as an Excel workbook.'
                            : 'Select a finalized payroll run to download the report as an Excel workbook.'}
                    </Typography>
                    <Stack spacing={2}>
                        {payrollReportType === 'reconciliation' && (
                            <FormControl fullWidth>
                                <InputLabel>Previous Payroll Run</InputLabel>
                                <Select value={selectedPreviousRun} label="Previous Payroll Run" onChange={(e) => setSelectedPreviousRun(e.target.value)}>
                                    {bankRuns.length === 0 && <MenuItem value="" disabled>No finalized runs found</MenuItem>}
                                    {bankRuns.map(run => (
                                        <MenuItem key={run.id} value={run.id}>
                                            {run.period_name} ({run.run_type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl fullWidth>
                            <InputLabel>{payrollReportType === 'reconciliation' ? 'Current Payroll Run' : 'Payroll Run'}</InputLabel>
                            <Select value={selectedRun} label={payrollReportType === 'reconciliation' ? 'Current Payroll Run' : 'Payroll Run'} onChange={(e) => setSelectedRun(e.target.value)}>
                                {bankRuns.length === 0 && <MenuItem value="" disabled>No finalized runs found</MenuItem>}
                                {bankRuns.map(run => (
                                    <MenuItem key={run.id} value={run.id}>
                                        {run.period_name} ({run.run_type})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setPayrollReportDialogOpen(false)} color="inherit" disabled={isDownloading}>Cancel</Button>
                    <Button onClick={handlePayrollReportDownload} variant="contained" disabled={isDownloading || !selectedRun || (payrollReportType === 'reconciliation' && (!selectedPreviousRun || selectedPreviousRun === selectedRun))}>
                        {isDownloading ? 'Generating Excel...' : 'Download'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bank Export Dialog */}
            <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Bank Payroll Export</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Export payroll data in specific bank formats for bulk transfers.
                    </Typography>
                    <Stack spacing={3}>
                        <FormControl fullWidth>
                            <InputLabel>Bank Format</InputLabel>
                            <Select value={selectedBankFormat} label="Bank Format" onChange={(e) => setSelectedBankFormat(e.target.value)}>
                                <MenuItem value="UAB">UAB Bank</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Payroll Run</InputLabel>
                            <Select value={selectedRun} label="Payroll Run" onChange={(e) => setSelectedRun(e.target.value)}>
                                {bankRuns.length === 0 && <MenuItem value="" disabled>No finalized runs found</MenuItem>}
                                {bankRuns.map(run => (
                                    <MenuItem key={run.id} value={run.id}>
                                        {run.period_name} ({run.run_type})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Company Debit Account (Optional)</InputLabel>
                            <Select 
                                value={selectedAccount} 
                                label="Company Debit Account (Optional)" 
                                onChange={(e) => setSelectedAccount(e.target.value)}
                            >
                                <MenuItem value=""><em>Skip / Leave Blank</em></MenuItem>
                                {bankAccounts.filter(a => a.bank_name.includes(selectedBankFormat)).map(acc => (
                                    <MenuItem key={acc.id} value={acc.id}>
                                        {acc.bank_name} - {acc.account_no} ({acc.account_name})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setBankDialogOpen(false)} color="inherit" disabled={isDownloading}>Cancel</Button>
                    <Button onClick={handleBankExport} variant="contained" disabled={isDownloading || !selectedRun || !selectedBankFormat}>
                        {isDownloading ? 'Generating Excel...' : 'Download'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReportsDashboard;
